import { getCookie } from "@insite/client-framework/Common/Cookies";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Icon from "@insite/mobius/Icon";
import Link, { LinkProps } from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Phone from "@ucs/Icons/Phone";
import Print from "@ucs/Icons/Print";
import SendFilled from "@ucs/Icons/SendFilled";
import UCSColors from "@ucs/Utils/UCSColors";
import React, { useEffect, useState } from "react";
import { css } from "styled-components";

const enum fields {
    alignment = "alignment",
    title = "title",
}

const enum icons {
    phone = "Phone",
    fax = "Fax",
    email = "Email",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.alignment]: "left" | "center" | "right";
        [fields.title]: string;
    };
    extendedStyles?: ContactStyles;
}

type Props = OwnProps;

export interface ContactStyles {
    titleWrapper?: InjectableCss;
    contactItem?: InjectableCss;
    emailIcon?: InjectableCss;
}

export const contactStyles: ContactStyles = {
    titleWrapper: {
        css: css`
            font-weight: 500;
            font-size: 18px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        border-top: 1px solid ${UCSColors.gray03};
                        padding-top: 13px;
                    `,
                    css`
                        border: none;
                    `,
                    css``,
                    css``,
                    css``,
                ]);
            }}
        `,
    },
    contactItem: {
        css: css`
            margin-top: 16px;
            font-size: 16px;
            color: ${UCSColors.gray08};
        `,
    },
    emailIcon: {
        css: css`
            margin-top: 2px;
            margin-right: 4px;
            vertical-align: sub;
        `,
    },
};

const FooterContatcSection: React.FC<Props> = ({ fields, extendedStyles }) => {
    const [styles] = useState(() => mergeToNew(contactStyles, extendedStyles));
    const [showEmail, setShowEmail] = useState(false);
    const [contactUsInfo, setContactUsInfo] = useState(getCookie("ContactUsInfoCookie") || "");
    const [salesEmail, setSalesEmail] = useState(getCookie("SalesEmailCookie") || "");
    const [phoneNumber1, setPhoneNumber1] = useState(getCookie("LocationPhoneNumber1") || "");
    const [phoneNumber2, setPhoneNumber2] = useState(getCookie("LocationPhoneNumber2") || "");
    const [fax, setFax] = useState(getCookie("LocationFax") || "");
 
    useEffect(() => {
        const timer = setTimeout(() => {
            if(!isLocationLoaded()){
                setContactUsInfo(getCookie("ContactUsInfoCookie") || "");
                setSalesEmail(getCookie("SalesEmailCookie") || "");
                setPhoneNumber1(getCookie("LocationPhoneNumber1") || "");
                setPhoneNumber2(getCookie("LocationPhoneNumber2") || "")
                setFax(getCookie("LocationFax") || "");
            }
        }, 4000);
        return () => clearTimeout(timer);
    }, []);

    const isLocationLoaded = () =>{
        return contactUsInfo || salesEmail || phoneNumber1 || phoneNumber2 || fax;
    }

    const GetIcon = (icon: string) => {
        switch (icon) {
            case icons.email:
                return SendFilled;
            case icons.phone:
                return Phone;
            case icons.fax:
                return Print;
            default:
                break;
        }
        return "";
    };

    const getCusomIcon = (icon: icons) => (
        <Icon src={GetIcon(icon)} color="primary" size={22} style={{ marginRight: 5, verticalAlign: "sub" }}></Icon>
    );

    // this formatting isn't reliable if the postal code won't have the space
    const getAddress = () => {
        const lastIndex = contactUsInfo.lastIndexOf(" ");
        const lastIndexSubstring = contactUsInfo.substring(0, lastIndex);
        const pcIndex = lastIndexSubstring.lastIndexOf(" ");
        const pcIndexSubstring = lastIndexSubstring.substring(0, pcIndex);
        const countryIndex = pcIndexSubstring.lastIndexOf(" ");
        const firstPart = contactUsInfo.substring(0, countryIndex);
        const lastPart = contactUsInfo.substring(countryIndex + 1, contactUsInfo.length);
        return { firstPart, lastPart };
    };

    return (
        <StyledWrapper style={{ textAlign: fields.alignment }}>
            <StyledWrapper {...styles.titleWrapper}>
                <Typography as="h3">{fields.title}</Typography>
            </StyledWrapper>

            {/* Change address and phones to be dynamic when US #683 done, and update typography styles to meet the formatting requirements */}
            {/* 7088 Financial Drive Mississauga, Ontario Canada L5N 7H5 */}
            <StyledWrapper {...styles.contactItem}>
                <Typography style={{ display: "inline-block", maxWidth: 200, marginBottom: 2 }}>
                    {contactUsInfo}
                </Typography>
            </StyledWrapper>


            {salesEmail && (
                <StyledWrapper {...styles.contactItem}>
                    <a
                        href={`mailto:${salesEmail}`}
                        onMouseEnter={() => setShowEmail(true)}
                        onMouseLeave={() => setShowEmail(false)}
                        // style={{ width: "100%",
                        //     display: "inline-block",
                        //     whiteSpace: "nowrap",
                        //     overflowX: "visible"
                        // }}
                    >
                        <Icon src={GetIcon(icons.email)} color="primary" size={22} {...styles.emailIcon}></Icon>
                        {"Email Us"}
                    </a>
                </StyledWrapper>
            )}

            {phoneNumber1 && (
                <StyledWrapper {...styles.contactItem}>
                    <a href={`tel:${phoneNumber1.replace(" ", "").replace("\n", "").replace("\t", "")}`}>
                        <Icon src={GetIcon(icons.phone)} color="primary" size={22} {...styles.emailIcon}></Icon>
                        {phoneNumber1}
                    </a>
                </StyledWrapper>
            )}

            {phoneNumber2 && (
                <StyledWrapper {...styles.contactItem}>
                    <a href={`tel:${phoneNumber2.replace(" ", "").replace("\n", "").replace("\t", "")}`}>
                        <Icon src={GetIcon(icons.phone)} color="primary" size={22} {...styles.emailIcon}></Icon>
                        {phoneNumber2}
                    </a>
                </StyledWrapper>
            )}

            {fax && (
                <StyledWrapper {...styles.contactItem}>
                    {getCusomIcon(icons.fax)}
                    {fax}
                </StyledWrapper> 
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: FooterContatcSection,
    definition: {
        group: "Footer",
        fieldDefinitions: [
            {
                name: fields.alignment,
                editorTemplate: "DropDownField",
                defaultValue: "left",
                fieldType: "General",
                options: [
                    {
                        displayName: "Left",
                        value: "left",
                    },
                    {
                        displayName: "Center",
                        value: "center",
                    },
                    {
                        displayName: "Right",
                        value: "right",
                    },
                ],
                hideEmptyOption: true,
            },
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "Contact us",
                fieldType: "Translatable",
            },
        ],
    },
};

export default widgetModule;
